/**
 * Hook for managing export table columns
 * Mirrors the QuickFillStore property pattern for column management
 */
import { useState, useCallback, useMemo } from 'react';
import { DEFAULT_COLUMNS } from '../exportPcfConstants';
import type { PropertyItem } from '../../../shared/ui/propertySelection/PropertySelectionPanel';

export interface ExportColumn {
  id: string;
  label: string;
  checked: boolean;
  revitParameter?: string;
  isExtra?: boolean;
  isReadOnly?: boolean;
  dataType?: string;
}

export interface UseExportColumnsReturn {
  /** All columns (default + extra) */
  allColumns: ExportColumn[];
  /** Only checked columns */
  checkedColumns: ExportColumn[];
  /** Extra columns added by user */
  extraColumns: ExportColumn[];
  /** Extra columns formatted for PropertySelectionPanel */
  panelExtraProperties: PropertyItem[];

  /** Add an extra column */
  addExtraColumn: (property: PropertyItem) => void;
  /** Remove an extra column */
  removeExtraColumn: (id: string) => void;
  /** Update checked state for a specific column */
  setColumnChecked: (id: string, checked: boolean) => void;
}

export function useExportColumns(): UseExportColumnsReturn {
  const [columns, setColumns] = useState<ExportColumn[]>(
    DEFAULT_COLUMNS.map(c => ({ ...c }))
  );
  const [extraColumns, setExtraColumns] = useState<ExportColumn[]>([]);

  const allColumns = useMemo(
    () => [...columns, ...extraColumns],
    [columns, extraColumns]
  );

  const checkedColumns = useMemo(
    () => allColumns.filter(c => c.checked),
    [allColumns]
  );

  const setColumnChecked = useCallback((id: string, checked: boolean) => {
    setColumns(prev =>
      prev.map(c => (c.id === id ? { ...c, checked } : c))
    );
    setExtraColumns(prev =>
      prev.map(c => (c.id === id ? { ...c, checked } : c))
    );
  }, []);

  const addExtraColumn = useCallback((property: PropertyItem) => {
    setExtraColumns(prev => {
      if (prev.some(c => c.id === property.id)) return prev;
      return [
        ...prev,
        {
          id: property.id,
          label: property.label,
          checked: true,
          revitParameter: property.revitParameter,
          isExtra: true,
          isReadOnly: property.isReadOnly,
          dataType: property.dataType,
        },
      ];
    });
  }, []);

  const removeExtraColumn = useCallback((id: string) => {
    setExtraColumns(prev => prev.filter(c => c.id !== id));
  }, []);

  const panelExtraProperties: PropertyItem[] = useMemo(
    () =>
      extraColumns.map(c => ({
        id: c.id,
        label: c.label,
        checked: c.checked,
        revitParameter: c.revitParameter,
        isReadOnly: c.isReadOnly,
        dataType: c.dataType,
      })),
    [extraColumns]
  );

  return {
    allColumns,
    checkedColumns,
    extraColumns,
    panelExtraProperties,
    addExtraColumn,
    removeExtraColumn,
    setColumnChecked,
  };
}
